import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import SummaryRow from '../components/SummaryRow';
import FinancialCard from '../components/FinancialCard';
import ProgressBar from '../components/ProgressBar';
import { dashboardAPI } from '../../../../services/dashboardService';

const PerformanceOverview = ({ onBack }) => {
    const { clinicId } = useParams();
    const [data, setData] = useState(null);
    const [periodStr, setPeriodStr] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                setLoading(true);
                const response = await dashboardAPI.getClinicMetrics(clinicId);
                setData(response.data);
                
                if (response.period && response.period.start_date) {
                    const date = new Date(response.period.start_date);
                    setPeriodStr(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
                }
            } catch (err) {
                setError(err.message || 'Failed to load metrics');
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchMetrics();
        }
    }, [clinicId]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Monthly Clinic Performance Overview" subtitle="Loading..." onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading metrics data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Monthly Clinic Performance Overview" subtitle="Error" onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
            </div>
        );
    }

    if (!data) return null;

    const health = data.clinic_health_summary;
    const financial = data.clinic_financial_summary;
    const outpatient = data.outpatient_visits_summary;
    const inpatient = data.inpatient_visits_summary;

    const kpis = [
        { title: 'Total Revenue', value: Number(health.total_revenue.value).toLocaleString(undefined, { minimumFractionDigits: 3 }), unit: 'JOD', change: `${Math.abs(health.total_revenue.growth)}%`, changeType: health.total_revenue.growth >= 0 ? 'up' : 'down' },
        { title: 'Total Visits', value: health.total_visits.value.toLocaleString(), change: `${Math.abs(health.total_visits.growth)}%`, changeType: health.total_visits.growth >= 0 ? 'up' : 'down' },
        { title: 'New Patients', value: health.new_patients.value.toLocaleString(), change: `${Math.abs(health.new_patients.growth)}%`, changeType: health.new_patients.growth >= 0 ? 'up' : 'down' },
    ];

    const outpatientRows = [
        { label: 'Outpatient Visits', value: outpatient.visits.value.toLocaleString(), change: `${Math.abs(outpatient.visits.growth)}%`, changeType: outpatient.visits.growth >= 0 ? 'up' : 'down' },
        { label: 'Gross Revenue', value: `${Number(outpatient.gross_revenue).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { label: 'Average Visit rate', value: `${Number(outpatient.average_visit_rate).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
    ];

    const inpatientRows = [
        { label: 'Inpatient Visits', value: inpatient.visits.value.toLocaleString(), change: `${Math.abs(inpatient.visits.growth)}%`, changeType: inpatient.visits.growth >= 0 ? 'up' : 'down' },
        { label: 'Gross Revenue', value: `${Number(inpatient.gross_revenue).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { label: 'Average Visit rate', value: `${Number(inpatient.average_visit_rate).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
    ];

    const financialItems = [
        { title: 'Consultation', value: `${Number(financial['Consultation']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { title: 'Medical Procedures', value: `${Number(financial['Medical Procedures']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { title: 'Lab and Imaging', value: `${Number(financial['Lab and Imaging']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { title: 'Vaccination', value: `${Number(financial['Vaccination']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { title: 'Medication', value: `${Number(financial['Medication']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
        { title: 'Others', value: `${Number(financial['Others']).toLocaleString(undefined, { minimumFractionDigits: 3 })} JOD` },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader title="Monthly Clinic Performance Overview" subtitle={periodStr} onBack={onBack} />

            <div className="clinic-dashboard-section-row">
                {/* Clinic Health Summary */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Clinic Health Summary</div>
                    <div className="clinic-dashboard-kpi-row">
                        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Outpatient Visits Summary */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Outpatient Visits Summary</div>
                    {outpatientRows.map((row, i) => <SummaryRow key={i} {...row} />)}
                    <ProgressBar label="Vet Utilization" value={outpatient.vet_utilization} />
                    <ProgressBar label="Follow-up rate" value={outpatient.follow_up_rate} />
                </div>
            </div>

            <div className="clinic-dashboard-section-row">
                {/* Clinic Financial Summary */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Clinic Financial Summary</div>
                    <div className="clinic-dashboard-financial-grid">
                        {financialItems.map((item, i) => <FinancialCard key={i} {...item} />)}
                    </div>
                </div>

                {/* Inpatient Visits Summary */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Inpatient Visits Summary</div>
                    {inpatientRows.map((row, i) => <SummaryRow key={i} {...row} />)}
                    <ProgressBar label="Vet Utilization" value={inpatient.vet_utilization} />
                    <ProgressBar label="Follow-up rate" value={inpatient.follow_up_rate} />
                    <ProgressBar label="Occupancy rate" value={inpatient.occupancy_rate} />
                    <ProgressBar label="Discharge %" value={inpatient.discharge_rate} />
                </div>
            </div>
        </div>
    );
};

export default PerformanceOverview;
